/** ISLANDS DRAWN BEFORE THE OPEN ANSWERED (client caching stage 2; Tom,
 *  2026-10-19: "revoked-access flash — acceptable for now"). A refresh draws
 *  an island from what this browser kept, then the open confirms it,
 *  replaces it, or refuses it and it is cleared. Until then it is
 *  UNCONFIRMED; when its open failed without an answer (offline, the server
 *  down, a timeout) it stays drawn, marked, and writes are paused: nothing is
 *  sent from a page the server has not confirmed (VC-core, 2026-10-19). */

import { setWriteGate } from "@sourcepilot/framework/write-gate";

const drawn = new Set<string>();
const offline = new Set<string>();
const listeners = new Set<() => void>();
let snapshot: { drawn: number; offline: number } = { drawn: 0, offline: 0 };

function changed(): void {
  snapshot = { drawn: drawn.size, offline: offline.size };
  for (const l of Array.from(listeners)) { try { l(); } catch { /* never breaks the others */ } }
}

export const OFFLINE_REASON = "This page shows what this browser kept, and the server has not confirmed it yet (offline). Changes are paused until it answers.";

setWriteGate(() => (offline.size > 0 ? OFFLINE_REASON : null));

/** The island was drawn from the store; its open has not answered. */
export function markDrawn(island: string): void {
  drawn.add(island);
  changed();
}

/** Its open answered (confirmed, replaced or refused): no longer drawn. */
export function markAnswered(island: string): void {
  const had = drawn.delete(island) || offline.delete(island);
  offline.delete(island);
  if (had) changed();
}

/** Its open failed without an answer: still drawn, now offline. */
export function markOffline(island: string): void {
  if (!drawn.has(island)) return;
  offline.add(island);
  changed();
}

export function isUnconfirmed(island: string): boolean {
  return drawn.has(island);
}

export function unconfirmedState(): { drawn: number; offline: number } {
  return snapshot;
}

export function onUnconfirmedChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** tests only */
export function resetUnconfirmedForTests(): void {
  drawn.clear();
  offline.clear();
  changed();
}
